// src/components/PublishChecklist/PublishChecklist.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../services/api";

/* ----- Vakio-labelit ja järjestys ----- */
const LABELS = {
  artist_type: "Esiintyjätyyppi – valitse esim. bilebändi, duo, trubaduuri tai DJ",
  name: "Esiintyjän nimi – lisää bändin, esiintyjän tai artistin nimi",
  slug: "Osoite (slug), määrittyy automaattisesti",
  profile_image: "Profiilikuva – lisää selkeä kuva esiintyjästä",
  location: "Paikkakunta – varmista, että lähtöpaikka on oikein",
  genres: "Musiikkityylit – valitse vähintään yksi tyyli",
  genre_ids: "Genret – varmista, että vähintään yksi tyyli on valittuna",
  sets: "Setit – määritä esityksen kesto(t) ja settien määrä",
  regions: "Keikka-alueet – valitse missä päin Suomea otat keikkoja",
  pricing: "Hinnat maakunnille – lisää hinnat valittuihin alueisiin",
  description: "Esiintyjän kuvaus – kirjoita myyvä kuvaus bändistä tai artistista",
  description_short: "Lyhyt kuvaus – lyhyt esittelyteksti listauksia varten",
  band_email: "Sähköposti – osoite, johon varauspyynnöt tulevat",
  band_phone: "Puhelinnumero – numero, johon tilaaja voi olla yhteydessä",
  socials: "Sosiaalisen median linkit – lisää esim. Instagram, Facebook, YouTube",
  // koosteavaimet (min-vaatimukset)
  gallery: "Galleria-kuvat – lisää vähintään yksi kuva galleriaan",
  media: "Video tai ääninäyte – lisää vähintään yksi video tai ääniklippi",
  // Fallback; varsinainen teksti muodostetaan dynaamisesti alempana
  calendar: "Kalenteri – merkitse vähintään 1 perjantai ja 1 lauantai 30 päivän sisälle",
};

const ORDER = [
  "name",
  "slug",
  "profile_image",
  "location",
  "genres",
  "sets",
  "regions",
  "pricing",
  "description",
  "band_email",
  "band_phone",
  "gallery",
  "media",
  "calendar",
];

function toLabel(key) {
  if (LABELS[key]) return LABELS[key];
  return key.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

/* --- Backend → kanoninen avain -aliaset --- */
const KEY_ALIASES = {
  // yhteystiedot
  email: "band_email",
  bandemail: "band_email",
  phone: "band_phone",
  bandphone: "band_phone",

  // minimi- ja synonyymiavaimet
  gallery_min: "gallery",
  images_min: "gallery",
  image_gallery_min: "gallery",

  video_or_audio: "media",
  media_min: "media",
  videos_min: "media",
  audios_min: "media",

  calendar_min: "calendar",
  pricing_min: "pricing",
  regions_min: "regions",
  sets_min: "sets",

  genreids: "genres",
  genres_ids: "genres",
  genre_ids: "genres",

  desc: "description",

  social_links: "socials",
  socials_min: "socials",
  social_media: "socials",
};

function normalizeMissing(raw) {
  const arr = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object"
      ? Object.entries(raw)
          .filter(([, v]) => v === false || v === 0 || v == null)
          .map(([k]) => k)
      : [];

  const mapped = arr.map((k) => {
    const key = String(k).trim().toLowerCase().replace(/[\s-]+/g, "_");
    return KEY_ALIASES[key] || key;
  });

  return Array.from(new Set(mapped));
}

/**
 * Props:
 * - bandId: number
 * - band: object               // nykyinen bändi-snapshot
 * - version?: number
 * - className?: string
 * - isPublished?: boolean
 * - isPending?: boolean
 * - status?: string
 * - onlyMissing?: boolean      // ⬅️ uusi: näytä vain puuttuvat kohdat
 */
export default function PublishChecklist({
  bandId,
  band,
  version = 0,
  className = "",
  isPublished = false,
  isPending = false,
  onlyMissing = false,     // näytä vain puuttuvat
  onlyDone = false,        // 👇 UUSI: näytä vain valmiit kohdat
  collapsible = false,     // 👇 UUSI: näytä listaus haitarina
  defaultOpen = false,     // 👇 UUSI: haitarin alkutila lista
  showRefresh = false,
  showMissingLead = true,           // ⬅️ UUSI: näytetäänkö “puuttuu”-teksti
  missingLeadText,                  // ⬅️ UUSI: yliaja teksti tarvittaessa
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // { ok, missing: string[] | object }
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(defaultOpen); // haitarin tila

// ID listalle (haitarin aria-controls)
const listId = useMemo(
  () => `chk-${bandId}-${onlyMissing ? "missing" : onlyDone ? "done" : "all"}`,
  [bandId, onlyMissing, onlyDone]
);
// (Halutessasi voit myös tehdä tämän ilman useMemoa:
// const listId = `chk-${bandId}-${onlyMissing ? "missing" : onlyDone ? "done" : "all"}`;

// --- YHTEINEN HEADER (käytetään molemmissa haaroissa) ---

  // Client-pohjainen puutehavainto nykyisestä band-snapshotista
  const clientMissing = useMemo(() => {
    if (!band) return [];

    const miss = [];

    const name = String(band.name || band.band_name || "").trim();
    if (!name) miss.push("name");

    const slug = String(band.slug || "").trim();
    if (!slug) miss.push("slug");

    const hasProfileImage = !!(
      band.profile_image ||
      band.profile_image_url ||
      band.placeholder_image ||
      band.placeholder_image_url
    );
    if (!hasProfileImage) miss.push("profile_image");

    const location = String(band.location || band.city || "").trim();
    if (!location) miss.push("location");

    const genresCount = Array.isArray(band.genre_ids)
      ? band.genre_ids.length
      : Array.isArray(band.genres)
      ? band.genres.length
      : 0;
    if (genresCount === 0) miss.push("genres");

    const setsCount = Array.isArray(band.sets)
      ? band.sets.filter(
          (s) =>
            (+s.set_count > 0 && +s.minutes_per_set > 0) ||
            String(s.label || "").trim()
        ).length
      : 0;
    if (setsCount === 0) miss.push("sets");

    // regions / pricing
    const regionIdsFromPricing = Array.isArray(band.pricing)
      ? new Set(
          band.pricing
            .map((p) => Number(p.region_id))
            .filter((n) => Number.isFinite(n))
        )
      : new Set();
    const regionsCount = Array.isArray(band.regions)
      ? band.regions.length
      : regionIdsFromPricing.size;
    if (regionsCount === 0) miss.push("regions");

    const hasAnyPrice =
      Array.isArray(band.pricing) &&
      band.pricing.some((p) => p.price != null);
    if (!hasAnyPrice) miss.push("pricing");

    const hasGallery = Array.isArray(band.gallery) && band.gallery.length > 0;
    if (!hasGallery) miss.push("gallery");

    const hasMedia =
      (Array.isArray(band.videos) && band.videos.length > 0) ||
      (Array.isArray(band.audio) && band.audio.length > 0);
    if (!hasMedia) miss.push("media");

    // Yhteystiedot
    const email = String(band.band_email || band.email || "").trim();
    if (!email) miss.push("band_email");

    const phone = String(band.band_phone || band.phone || "").trim();
    if (!phone) miss.push("band_phone");

    const desc = String(band.description || "").trim();
    if (!desc) miss.push("description");

    return miss;
  }, [band]);

  const doFetch = useCallback(async () => {
    if (!Number.isFinite(Number(bandId))) return;
    setLoading(true);
    setError(null);
    try {
      const res = await (api.reviewCheck
        ? api.reviewCheck(bandId)
        : fetch(`/api/bands/${bandId}/review-check`, {
            credentials: "include",
          }).then((r) => r.json()));
      const json = res?.data ?? res?.result ?? res;
      setData(json);
    } catch {
      setError("Tarkistusta ei voitu hakea.");
    } finally {
      setLoading(false);
    }
  }, [bandId]);

  useEffect(() => {
    doFetch();
  }, [doFetch, version]);

    // Päivitä kun selainikkuna saa fokuksen
    useEffect(() => {
    const onFocus = () => doFetch();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    }, [doFetch]);

  // Normalisoi serverin puuttuvat + yhdistä client-puutteisiin
  const missingSet = useMemo(() => {
    const serverMissing = normalizeMissing(data?.missing);
    const s = new Set([...serverMissing, ...clientMissing]);

    // Sääntö: ei alueita → myös setit ja hinnat puuttuu
    if (s.has("regions")) {
      s.add("sets");
      s.add("pricing");
    }
    return s;
  }, [data?.missing, clientMissing]);

  // Rakenna rivit: ORDER + muut puuttuvat ilman duplikaatteja
  const items = useMemo(() => {
    const extra = Array.from(missingSet).filter((k) => !ORDER.includes(k));
    const keys = [...ORDER, ...extra];
    return keys.map((key) => ({
      key,
      label: toLabel(key),
      done: !missingSet.has(key),
    }));
  }, [missingSet]);

  const visibleItems = useMemo(() => {
    let list = items;
    if (onlyMissing) list = items.filter((it) => !it.done);
    else if (onlyDone) list = items.filter((it) => it.done);
    return list;
  }, [items, onlyMissing, onlyDone]);

  // --- Johdantoteksti (mukautuu julkaisu-/pending-tilaan sekä onlyMissing-tilaan) ---
  const lead = useMemo(() => {
    if (error) return null;
    if (!data) {
      return onlyMissing ? "Ladataan puuttuvia kohtia…" : "Ladataan tarkistusta…";
    }

       if (onlyDone) {
     return missingSet.size === 0
       ? "Kaikki kunnossa – kaikki kohdat valmiit."
       : "Alla listatut kohdat ovat valmiit. Puuttuvat näet vasemmalla.";
   }

    if (onlyMissing) {
      return missingSet.size === 0
        ? "Kaikki kunnossa – mikään ei puutu."
        : "Täydennä alla listatut puuttuvat kohdat.";
    }

    if (isPublished) {
      return missingSet.size > 0
        ? "Profiili on julkaistu. Suosittelemme täydentämään puuttuvat kohdat alla."
        : "Profiili on julkaistu. Voit päivittää sisältöä milloin vain.";
    }
    if (isPending) {
      return "Profiili odottaa hyväksyntää. Voit vielä täydentää tietoja; päivitykset huomioidaan tarkistuksessa.";
    }
    return data.ok && missingSet.size === 0
      ? "Kaikki kunnossa. Voit lähettää profiilin tarkistettavaksi."
      : "Täydennä puuttuvat kohdat (merkattu punaisella).";
  }, [data, error, isPublished, isPending, missingSet, onlyMissing, onlyDone]);

  const leadClass = useMemo(() => {
    if (!data || error) return "text-xs text-black/60 dark:text-white/60 mb-2";

       if (onlyDone) {
     return missingSet.size === 0
       ? "text-xs text-emerald-700 dark:text-emerald-400 mb-2"
       : "text-xs text-black/60 dark:text-white/60 mb-2";
   }

    if (onlyMissing) {
      return missingSet.size === 0
        ? "text-xs text-emerald-700 dark:text-emerald-400 mb-2"
        : "text-xs text-black/60 dark:text-white/60 mb-2";
    }
    if (isPublished) return "text-xs text-emerald-700 dark:text-emerald-400 mb-2";
    if (isPending) return "text-xs text-amber-700 dark:text-amber-400 mb-2";
    return missingSet.size === 0
      ? "text-xs text-emerald-700 dark:text-emerald-400 mb-2"
      : "text-xs text-black/60 dark:text-white/60 mb-2";
  }, [data, error, isPublished, isPending, missingSet, onlyMissing, onlyDone]);

  // --- YHTEINEN HEADER ---
const Header = () => (
  <div className="flex items-center justify-between mb-2">
    <h3 className="dark:text-white/90 text-black/90 font-customFont2 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm mb-1 font-bold">
      Julkaisun tarkistus
    </h3>
    <div className="flex items-center gap-3">
      {collapsible && (
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          aria-expanded={open}
          aria-controls={listId}
          className="dark:text-white/80 text-black/80 font-customFont1 text-xs underline hover:opacity-80"
        >
          {open ? "Piilota kohdat" : "Näytä kohdat"}
        </button>
      )}
     {(showRefresh || error) && (
        <button
            type="button"
            onClick={doFetch}
            disabled={loading}
            className="dark:text-white/80 text-black/80 font-customFont1 text-xs underline hover:opacity-80 disabled:opacity-50"
        >
            {loading ? "Päivitetään…" : error ? "Yritä uudelleen" : "Päivitä"}
        </button>
        )}

    </div>
  </div>
);

// Jos onlyMissing ja ei puuttuvia → älä renderöi mitään
if (onlyMissing && visibleItems.length === 0) {
  return null;
}

  // Jos onlyMissing ja ei puuttuvia → älä renderöi mitään
  if (onlyMissing && visibleItems.length === 0) {
    return null;
  }

  // Profiili on julkaistu → piilota koko checklist.
  // Kun profiili piilotetaan (isPublished = false), checklist tulee takaisin näkyviin.
  if (isPublished && !isPending) {
    return null;
  }

  return (
    <div
      className={[
        "rounded-lg border border-black/10 dark:border-white/10 bg-white/60 dark:bg-black/40 p-4",
        className,
      ].join(" ")}
    >
      {!onlyMissing && <Header />}

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 mb-2">
          {error}
        </p>
      )}
        {!error && !onlyMissing && lead && <p className={leadClass}>{lead}</p>}

     {/* onlyMissing: kevyt johdantoteksti (mobiiliin) */}
      {onlyMissing && visibleItems.length > 0 && showMissingLead && (
        <p className="dark:text-white/90 text-black/90 font-customFont2 text-lg font-bold mb-2">
          {missingLeadText ?? "Sinulta puuttuu vielä:"}
        </p>
      )}



{/* LISTA – näytä aina onlyMissing-tilassa, muuten haitarin mukaan */}
{(onlyMissing || !collapsible || open) && (
    <ul id={listId} className="space-y-1">
    {visibleItems.map(({ key, label, done }) => (
        <li
        key={key}
        // Yleiskoko riveille
        className="flex items-center gap-2 font-customFont1 text-sm sm:text-base"
        >
        <span
            aria-hidden
            className={[
            "inline-flex h-4 w-4 rounded-full border",
            done ? "bg-emerald-500 border-emerald-500" : "bg-red-500 border-red-500",
            ].join(" ")}
        />
        {/* Vain punaisille oma koko */}
        <span
            className={
            done
                ? "text-black/80 dark:text-white/80"
                : "text-red-700 dark:text-red-400 xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm font-customFont1"
            }
        >
            {label}
        </span>
        </li>
    ))}
    </ul>

)}


    </div>
  );
}
